import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  axiosInstance,
  CalculatePolicyQuoteOutput,
  CustomFieldDto,
  type PriceBreakdownOutputDto,
} from 'korint-sdk';
import { useDebouncedValue } from 'korint-sdk/react';
import { useMemo } from 'react';
import { type ManualComponentValuesMap, serializeManualValues } from './manual-components';

export type PriceBreakdown = PriceBreakdownOutputDto;

export type AvailablePricingPeril = {
  id: string;
  included: boolean;
  options?: string[];
  customFields?: string[];
  defaultCustomFieldValues?: Array<{ key: string; value: unknown }>;
};

export type AvailablePricingTier = {
  tierId: string;
  pricing: CalculatePolicyQuoteOutput;
  breakdown?: PriceBreakdown;
  perils: AvailablePricingPeril[];
};

type AvailablePricingResponse = {
  tiers: AvailablePricingTier[];
  defaultTier: string;
  selectedTier?: string;
  legacyMode?: boolean;
};

type UseAvailablePricingOptions = {
  branchId?: string;
  enabled?: boolean;
  policyCustomFields?: CustomFieldDto[];
  assetCustomFields?: CustomFieldDto[];
  policyCustomFieldsInputs?: Record<string, unknown>;
  manualComponentValues?: ManualComponentValuesMap;
};

const DEBOUNCE_MS = 300;

function stringifyIfNonEmpty(value: Record<string, unknown> | undefined): string | undefined {
  return value && Object.keys(value).length > 0 ? JSON.stringify(value) : undefined;
}

export function useAvailablePricing(policyId: string, options?: UseAvailablePricingOptions) {
  const debouncedPolicyCustomFields = useDebouncedValue(options?.policyCustomFields, DEBOUNCE_MS);
  const debouncedAssetCustomFields = useDebouncedValue(options?.assetCustomFields, DEBOUNCE_MS);

  const policyCustomFieldsInputsJson = useMemo(
    () => stringifyIfNonEmpty(options?.policyCustomFieldsInputs),
    [options?.policyCustomFieldsInputs],
  );
  const debouncedPerilParametersJson = useDebouncedValue(policyCustomFieldsInputsJson, DEBOUNCE_MS);

  const manualComponentValuesJson = useMemo(
    () => serializeManualValues(options?.manualComponentValues),
    [options?.manualComponentValues],
  );
  const debouncedManualValuesJson = useDebouncedValue(manualComponentValuesJson, DEBOUNCE_MS);

  return useQuery<AvailablePricingResponse>({
    queryKey: [
      'available-pricing',
      policyId,
      options?.branchId,
      debouncedPolicyCustomFields,
      debouncedAssetCustomFields,
      debouncedPerilParametersJson,
      debouncedManualValuesJson,
    ],
    queryFn: async () => {
      const params: Record<string, unknown> = {};
      if (options?.branchId) params.branchId = options.branchId;
      if (debouncedPolicyCustomFields?.length) {
        params.policyCustomFields = JSON.stringify(debouncedPolicyCustomFields);
      }
      if (debouncedAssetCustomFields?.length) {
        params.assetCustomFields = JSON.stringify(debouncedAssetCustomFields);
      }
      if (debouncedPerilParametersJson) {
        params.policyCustomFieldsInputs = debouncedPerilParametersJson;
      }
      if (debouncedManualValuesJson) {
        params.manualComponentValues = debouncedManualValuesJson;
      }

      const response = await axiosInstance.get<AvailablePricingResponse>(
        `/view-specific/${policyId}/available-pricing`,
        { params },
      );
      return response.data;
    },
    placeholderData: keepPreviousData,
    enabled: options?.enabled !== false,
  });
}
